import React from 'react';
import Interweave from 'interweave';

import logger from 'logger';
import ApiService from 'services/ApiService/ApiService';
import ConfigurationService, { ConfigurationKeys } from 'services/ConfigurationService';
import getMessagingInstance from 'services/MessagingService/MessagingService';
import {
  TRANSLATIONS_KEY_INVALID,
  TRANSLATIONS_KEY_MISSING,
  TRANSLATIONS_LOAD_ERROR,
  TRANSLATIONS_LOADED,
} from 'services/MessagingService/EventNames';


const I18N_PATH = ConfigurationService.get(ConfigurationKeys.I18N);
const HTMLTagsPattern = /(&[^\s]*;|<\/?\w+((\s+\w+(\s*=\s*(?:".*?"|'.*?'|[\^'">\s]+))?)+\s*|\s*)\/?>)/;

class I18nService {

  messagingService;

  constructor() {
    this.messagingService = getMessagingInstance();
  }

  loadTranslations = (): Promise<void> => {
    const localeUrl = this.getLocaleUrl();
    const url = `${localeUrl}`;

    return ApiService.get(url, { withCredentials: false })
      .then((res) => {
        window.i18nDictionary = res.data;
        this.messagingService.emit(TRANSLATIONS_LOADED);
      })
      .catch((err) => {
        this.messagingService.emit(TRANSLATIONS_LOAD_ERROR, { error: err });
        logger.error({ msg: 'Could not load translations', error: err });

        return Promise.reject(err);
      });
  };

  getLocaleUrl = (): string => {
    const browserLang = navigator.language;
    let locale = window.LOCALE;
    const isSpain =
      (locale === 'es' && browserLang.toLocaleLowerCase() === 'es-es') ||
      browserLang.toLocaleLowerCase() === 'es' ||
      browserLang.toLocaleLowerCase() === 'ca';
    // chose spanish(Spain) or spanish(Latin)
    if (isSpain) {
      locale = 'es_ES';
    }
    if (locale && window.TRANSLATIONS[`v2_i18n_${locale}`]) {
      return I18N_PATH + window.TRANSLATIONS[`v2_i18n_${locale}`];
    }

    return I18N_PATH + window.TRANSLATIONS['v2_i18n'];
  };

  translate = (
    key?: string,
    params?: { [key: number | string]: any },
    defaultValue?: string
  ): string | JSX.Element => {
    if (!key) {
      this.messagingService.emit(TRANSLATIONS_KEY_INVALID, { key });

      return 'Nothing to translate';
    }
    const translationString = window.i18nDictionary[key];

    if (!translationString) {
      if (!defaultValue) {
        logger.error({ msg: 'Translation error for key', error: key });
      }
      this.messagingService.emit(TRANSLATIONS_KEY_MISSING, { key, hasDefault: !!defaultValue });

      return defaultValue ? defaultValue : `Missing translation key: ${key}`;
    }

    if (!params) {
      return this.parseHtml(translationString);
    }

    let template = translationString;

    Object.keys(params).forEach((value) => {
      if (value && template) {
        template = template.split(`{${value}}`).join(params[value]);
      }
    });

    return this.parseHtml(template) || `Missing translation key: ${key}`;
  };

  parseHtml = (str: string): JSX.Element | string => {
    if (this.hasHtmlTags(str)) {
      return <Interweave content={str} />;
    }

    return str;
  };

  hasHtmlTags = (value: string): boolean => value?.search(HTMLTagsPattern) >= 0;

  getActiveLanguageCode = () => window.LOCALE;
}

const i18n = new I18nService();

export default i18n;

export const { getActiveLanguageCode, loadTranslations, translate } = i18n;
